import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import SignatureIcon from '@atlaskit/icon-lab/core/signature';
import { fg } from '@atlaskit/platform-feature-flags';

import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { filter as salesforceStageFilter } from '../../../../../controllers/store/search/filters/salesforce-stage';

import { i18n } from './messages';

const SalesforceStageFilter = ({
	isFilterInDropdown,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: salesforceStageFilter.id,
	});

	// When the dynamic stage filter flag is on, eagerly fetch the stage options as
	// soon as the filter is rendered — not just when the user opens the dropdown.
	// The store action (loadInitialSelectFilterOptions) derives the datasource from
	// thirdPartyConfigs when selectedDatasource is not set via URL params.
	// The ref guards against re-triggering when loadInitialOptions gets a new
	// reference on re-renders (it is not memoised in the store).
	const hasLoadedInitialOptions = useRef(false);
	useEffect(() => {
		if (!hasLoadedInitialOptions.current && fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter')) {
			hasLoadedInitialOptions.current = true;
			loadInitialOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!filterData) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	const allOptions = [...selectedOptions, ...suggestedOptions];

	const label =
		fit === 'compact' && selectedOptions.length > 0
			? ''
			: formatMessage(i18n.salesforceStageFilterLabel);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={allOptions}
			selectedOptions={selectedOptions}
			label={label}
			filterOrControlId={'salesforceStage'}
			isLoading={isLoading}
			isSearchable
			noOptionsMessage={formatMessage(i18n.salesforceStageFilterNoOptions)}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: salesforceStageFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={<SignatureIcon label="" />}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default SalesforceStageFilter;
